import React, { useState, useEffect } from 'react'
import { 
  Star,
  Clock,
  Search,
  Download,
  MoreHorizontal,
  Eye,
  Phone,
  MapPin,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Award,
  TrendingUp,
  Users,
  StarOff,
  Loader2
} from 'lucide-react'
import { format } from 'date-fns'
import { useNavigate } from 'react-router-dom'
import ApiService from '../services/api'
import toast from 'react-hot-toast'

function FeaturedPandits() {
  const navigate = useNavigate()
  const [featuredPandits, setFeaturedPandits] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [filterRating, setFilterRating] = useState('all')
  const [filterLocation, setFilterLocation] = useState('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(12)
  const [stats, setStats] = useState({
    totalFeatured: 0,
    totalRevenue: 0,
    avgRating: 0,
    totalBookings: 0
  })
  
  // Loading states for individual actions
  const [exportLoading, setExportLoading] = useState(false)
  const [removingFeatured, setRemovingFeatured] = useState({})
  const [editingProfile, setEditingProfile] = useState({})
  const [contactingPandit, setContactingPandit] = useState({})

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 500)
    return () => clearTimeout(timer)
  }, [searchTerm])

  // Reset to first page when search term changes
  useEffect(() => {
    setCurrentPage(1)
  }, [debouncedSearchTerm, filterRating, filterLocation])

  // Load featured pandits data
  useEffect(() => {
    fetchFeaturedPandits()
  }, [])

  const fetchFeaturedPandits = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await ApiService.getFeaturedPandits()
      
      if (response.success) {
        setFeaturedPandits(response.data || [])
        // Calculate stats
        const pandits = response.data || []
        const totalRating = pandits.reduce((sum, p) => sum + (p.profile?.ratingAvg || 0), 0)
        const avgRating = pandits.length > 0 ? (totalRating / pandits.length).toFixed(1) : 0
        
        setStats({
          totalFeatured: pandits.length,
          avgRating: avgRating,
          totalBookings: pandits.reduce((sum, p) => sum + (p.profile?.ratingCount || 0), 0),
          totalRevenue: pandits.reduce((sum, p) => sum + (p.pricing?.[0]?.price || 0), 0)
        })
      } else {
        throw new Error(response.message || 'Failed to fetch featured pandits')
      }
    } catch (error) {
      console.error('Error fetching featured pandits:', error)
      setError(error.message || 'Failed to load featured pandits')
      toast.error('Failed to load featured pandits')
      setFeaturedPandits([])
      setStats({
        totalFeatured: 0,
        avgRating: 0,
        totalBookings: 0,
        totalRevenue: 0
      })
    } finally {
      setLoading(false)
    }
  }

  const handleRemoveFromFeatured = async (panditId) => {
    try {
      setRemovingFeatured(prev => ({ ...prev, [panditId]: true }))
      await ApiService.updatePandit(panditId, { featuredPandit: false })
      toast.success('Pandit removed from featured list')
      fetchFeaturedPandits() // Refresh the list
    } catch (error) {
      console.error('Error removing from featured:', error)
      toast.error('Failed to remove pandit from featured list')
    } finally {
      setRemovingFeatured(prev => ({ ...prev, [panditId]: false }))
    }
  }

  const exportFeaturedPandits = async () => {
    try {
      setExportLoading(true)
      
      // Simulate processing time for better UX
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      const csvContent = "data:text/csv;charset=utf-8," 
        + "Name,Email,Phone,City,State,Rating,Experience,Services\n"
        + featuredPandits.map(p => 
            `"${p.profile?.name}","${p.user?.email}","${p.user?.phone}","${p.profile?.city}","${p.profile?.state}","${p.profile?.ratingAvg}","${p.profile?.experience}","${p.expertise?.map(e => e.service?.name).join('; ')}"`
          ).join("\n")

      const encodedUri = encodeURI(csvContent)
      const link = document.createElement("a")
      link.setAttribute("href", encodedUri)
      link.setAttribute("download", `featured_pandits_${format(new Date(), 'yyyy-MM-dd')}.csv`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      toast.success('Featured pandits list exported successfully!')
    } catch (error) {
      console.error('Error exporting data:', error)
      toast.error('Failed to export featured pandits list')
    } finally {
      setExportLoading(false)
    }
  }

  const handleViewProfile = (panditId) => {
    navigate(`/pandits/${panditId}`)
  }

  const handleEditProfile = async (panditId) => {
    try {
      setEditingProfile(prev => ({ ...prev, [panditId]: true }))
      // Simulate API call - replace with actual edit functionality
      await new Promise(resolve => setTimeout(resolve, 1500))
      toast.success('Profile edit functionality coming soon!')
    } catch (error) {
      console.error('Error editing profile:', error)
      toast.error('Failed to open profile editor')
    } finally {
      setEditingProfile(prev => ({ ...prev, [panditId]: false }))
    }
  }

  const handleContactPandit = async (pandit) => {
    try {
      setContactingPandit(prev => ({ ...prev, [pandit.id]: true }))
      
      // Create mailto link with pandit details
      const subject = encodeURIComponent(`Contact from VaidikAdmin - Featured Pandit Inquiry`)
      const body = encodeURIComponent(
        `Dear ${pandit.profile?.name || 'Pandit'},\n\n` +
        `I am contacting you regarding your featured listing on our platform.\n\n` +
        `Best regards,\nVaidikAdmin Team`
      )
      const mailtoLink = `mailto:${pandit.user?.email}?subject=${subject}&body=${body}`
      
      // Simulate processing time
      await new Promise(resolve => setTimeout(resolve, 800))
      
      window.open(mailtoLink, '_blank')
      toast.success('Email client opened successfully!')
    } catch (error) {
      console.error('Error contacting pandit:', error)
      toast.error('Failed to open email client')
    } finally {
      setContactingPandit(prev => ({ ...prev, [pandit.id]: false }))
    }
  }

  // Filter pandits based on search and filters
  const filteredPandits = featuredPandits.filter(pandit => {
    const matchesSearch = !debouncedSearchTerm || 
      pandit.profile?.name?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
      pandit.user?.email?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
      pandit.profile?.city?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
      pandit.expertise?.some(e => e.service?.name?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()))

    const matchesRating = filterRating === 'all' || 
      (filterRating === '4.5+' && pandit.profile?.ratingAvg >= 4.5) ||
      (filterRating === '4.0+' && pandit.profile?.ratingAvg >= 4.0) ||
      (filterRating === '3.5+' && pandit.profile?.ratingAvg >= 3.5)

    const matchesLocation = filterLocation === 'all' || 
      pandit.profile?.state === filterLocation

    return matchesSearch && matchesRating && matchesLocation
  })

  // Pagination
  const totalPages = Math.ceil(filteredPandits.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentPandits = filteredPandits.slice(startIndex, endIndex)

  // Get unique states for filter
  const uniqueStates = [...new Set(featuredPandits.map(p => p.profile?.state).filter(Boolean))]

  const statCards = [
    {
      title: 'Featured Pandits',
      value: stats.totalFeatured,
      icon: Star,
      color: 'bg-yellow-500',
      change: '+8%',
      changeType: 'positive',
      description: 'Premium listings'
    },
    {
      title: 'Average Rating',
      value: stats.avgRating,
      icon: Award,
      color: 'bg-green-500',
      change: '+0.2',
      changeType: 'positive',
      description: 'Overall rating'
    },
    {
      title: 'Total Bookings',
      value: stats.totalBookings.toLocaleString(),
      icon: TrendingUp,
      color: 'bg-blue-500',
      change: '+15%',
      changeType: 'positive',
      description: 'Completed bookings'
    },
    {
      title: 'Revenue Impact',
      value: `₹${(stats.totalRevenue / 1000).toFixed(0)}K`,
      icon: Users,
      color: 'bg-purple-500',
      change: '+22%',
      changeType: 'positive',
      description: 'Monthly revenue'
    }
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            Featured Pandits
          </h1>
          <p className="mt-2 text-gray-600">
            Manage premium pandit listings and featured placements
          </p>
          <div className="mt-1 text-sm text-yellow-600 font-medium">
            Premium spiritual guides with highest ratings
          </div>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-3">
          <button
            onClick={exportFeaturedPandits}
            disabled={exportLoading}
            className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {exportLoading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            {exportLoading ? 'Exporting...' : 'Export'}
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon
          return (
            <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-16 h-16 opacity-5">
                <div className="w-full h-full bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full transform translate-x-8 -translate-y-8"></div>
              </div>
              
              <div className="flex items-center justify-between relative z-10">
                <div>
                  <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">{stat.value}</p>
                  <p className="text-xs text-gray-500 mt-1">{stat.description}</p>
                </div>
                <div className={`${stat.color} p-3 rounded-lg shadow-sm`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
              </div>
              <div className="mt-4 flex items-center">
                <span className={`text-sm font-medium ${
                  stat.changeType === 'positive' ? 'text-green-600' : 'text-red-600'
                }`}>
                  {stat.change}
                </span>
                <span className="text-sm text-gray-500 ml-2">from last month</span>
              </div>
            </div>
          )
        })}
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search featured pandits..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full sm:w-64 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
              />
            </div>
            
            <select
              value={filterRating}
              onChange={(e) => setFilterRating(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
            >
              <option value="all">All Ratings</option>
              <option value="4.5+">4.5+ Stars</option>
              <option value="4.0+">4.0+ Stars</option>
              <option value="3.5+">3.5+ Stars</option>
            </select>
            
            
          </div>
          
          <div className="text-sm text-gray-600">
            Showing {currentPandits.length} of {filteredPandits.length} featured pandits
          </div>
        </div>
      </div>

      {/* Featured Pandits Grid */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <Star className="w-5 h-5 mr-2 text-yellow-600" />
            Premium Spiritual Guides
          </h3>
          <p className="text-sm text-gray-600 mt-1">Top-rated pandits with featured placement</p>
        </div>
        
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-600 mx-auto"></div>
            <p className="mt-2 text-gray-500">Loading featured pandits...</p>
          </div>
        ) : error ? (
          <div className="p-8 text-center">
            <div className="text-red-500 mb-4">
              <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Featured Pandits</h3>
            <p className="text-gray-500 mb-4">{error}</p>
            <button
              onClick={fetchFeaturedPandits}
              className="inline-flex items-center px-4 py-2 bg-yellow-600 text-white font-medium rounded-lg hover:bg-yellow-700 transition-colors duration-200"
            >
              Try Again
            </button>
          </div>
        ) : currentPandits.length === 0 ? (
          <div className="p-8 text-center">
            <Star className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No featured pandits found</p>
            {debouncedSearchTerm && (
              <p className="text-sm text-gray-400 mt-2">Try adjusting your search criteria</p>
            )}
          </div>
        ) : (
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {currentPandits.map((pandit) => (
                <div key={pandit.id} className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl border-2 border-yellow-200 p-6 hover:shadow-lg transition-all duration-200 relative overflow-hidden">
                  {/* Featured Badge */}
                  <div className="absolute top-0 right-0 bg-yellow-500 text-white px-3 py-1 rounded-bl-lg text-xs font-bold">
                    <Star className="w-3 h-3 inline mr-1" />
                    FEATURED
                  </div>
                  
                  {/* Pandit Info */}
                  <div className="flex items-start space-x-4 mb-4">
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-r from-yellow-400 to-orange-500 flex items-center justify-center relative shadow-lg">
                        <span className="text-white font-bold text-lg">
                          {pandit.profile?.name?.charAt(0)?.toUpperCase() || 'P'}
                        </span>
                        <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full border-2 border-white"></div>
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-bold text-gray-900 truncate">
                        {pandit.profile?.name || 'No name provided'}
                      </h3>
                      <p className="text-sm text-gray-600 truncate">
                        {pandit.user?.email}
                      </p>
                      {pandit.expertise?.[0] && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 mt-1">
                          {pandit.expertise[0].service?.name}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Rating and Experience */}
                  <div className="space-y-3 mb-4">
                    {pandit.profile?.ratingAvg && (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-1">
                          <Star className="w-4 h-4 text-yellow-500 fill-current" />
                          <span className="font-semibold text-gray-900">{pandit.profile.ratingAvg}</span>
                          <span className="text-sm text-gray-500">
                            ({pandit.profile.ratingCount || 0} reviews)
                          </span>
                        </div>
                        <div className="text-sm text-gray-600 flex items-center">
                          <Clock className="w-3 h-3 mr-1" />
                          {pandit.profile?.experience || 0}y exp
                        </div>
                      </div>
                    )}
                    
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center text-gray-600">
                        <MapPin className="w-3 h-3 mr-1" />
                        {pandit.profile?.city}, {pandit.profile?.state}
                      </div>
                      {pandit.pricing?.[0] && (
                        <div className="font-semibold text-green-600">
                          ₹{pandit.pricing[0].price}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Languages */}
                  {pandit.profile?.languages && (
                    <div className="mb-4">
                      <div className="flex flex-wrap gap-1">
                        {pandit.profile.languages.map((lang, index) => (
                          <span key={index} className="px-2 py-1 bg-white rounded text-xs text-gray-600 border border-gray-200">
                            {lang}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Actions */}
                  <div className="flex items-center space-x-2">
                    <button 
                      onClick={() => handleViewProfile(pandit.id)}
                      className="flex-1 bg-white border border-gray-300 text-gray-700 px-3 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors duration-200"
                    >
                      <Eye className="w-4 h-4 inline mr-1" />
                      View Profile
                    </button>
                    <div className="relative group">
                      <button className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-white transition-colors duration-200">
                        <MoreHorizontal className="w-4 h-4" />
                      </button>
                      <div className="absolute right-0 bottom-full mb-2 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                        <div className="py-1">
                          <button
                            onClick={() => handleRemoveFromFeatured(pandit.id)}
                            disabled={removingFeatured[pandit.id]}
                            className="block w-full text-left px-4 py-2 text-sm text-red-700 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {removingFeatured[pandit.id] ? (
                              <Loader2 className="w-4 h-4 inline mr-2 animate-spin" />
                            ) : (
                              <StarOff className="w-4 h-4 inline mr-2" />
                            )}
                            {removingFeatured[pandit.id] ? 'Removing...' : 'Remove Featured'}
                          </button>

                          <button 
                            onClick={() => handleContactPandit(pandit)}
                            disabled={contactingPandit[pandit.id]}
                            className="block w-full text-left px-4 py-2 text-sm text-green-700 hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {contactingPandit[pandit.id] ? (
                              <Loader2 className="w-4 h-4 inline mr-2 animate-spin" />
                            ) : (
                              <Phone className="w-4 h-4 inline mr-2" />
                            )}
                            {contactingPandit[pandit.id] ? 'Opening...' : 'Contact Pandit'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Member since */}
                  <div className="mt-3 pt-3 border-t border-yellow-200 text-xs text-gray-500 flex items-center">
                    <Calendar className="w-3 h-3 mr-1" />
                    Featured since {format(new Date(pandit.createdAt), 'MMM yyyy')}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pagination */}
        {currentPandits.length > 0 && totalPages > 1 && (
          <div className="bg-gray-50 px-6 py-3 border-t border-gray-200 rounded-b-xl">
            <div className="flex items-center justify-between">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(prev => prev + 1)}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing{' '}
                    <span className="font-medium">{startIndex + 1}</span>{' '}
                    to{' '}
                    <span className="font-medium">{Math.min(endIndex, filteredPandits.length)}</span>{' '}
                    of{' '}
                    <span className="font-medium">{filteredPandits.length}</span>{' '}
                    results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum
                      if (totalPages <= 5) {
                        pageNum = i + 1
                      } else if (currentPage <= 3) {
                        pageNum = i + 1
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i
                      } else {
                        pageNum = currentPage - 2 + i
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            currentPage === pageNum
                              ? 'z-10 bg-yellow-50 border-yellow-500 text-yellow-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      )
                    })}
                    
                    <button
                      onClick={() => setCurrentPage(prev => prev + 1)}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default FeaturedPandits